/**
 * Car categories: cars grouped by measured Time Attack pace, so a board can
 * rank cars that are a fair fight for each other.
 *
 * Shared by the game and the Worker. The Worker reads a category's cars from
 * here when it serves a category board, so moving a car between categories
 * takes its past runs with it -- nothing about a category is stored with a run.
 *
 * Pace is the bot's lap time in Time Attack Easy against the reference car,
 * averaged over every live route (src/bot/pace.ts). The reference is a fixed
 * car rather than the fastest one, so a quicker newcomer does not shift every
 * band. tests/categories.test.mjs holds each car inside its category's band;
 * `npm run cars:pace` prints where every car stands.
 */

export type CategoryId = 'gt' | 'sport' | 'street';

export interface Category {
  id: CategoryId;
  name: string;
  /** Per cent slower than the reference car: the band is [minGap, maxGap). */
  minGap: number;
  maxGap: number;
}

/** Fastest first. */
export const CATEGORIES: readonly Category[] = [
  { id: 'gt', name: 'GT', minGap: -Infinity, maxGap: 5 },
  { id: 'sport', name: 'SPORT', minGap: 5, maxGap: 14 },
  { id: 'street', name: 'STREET', minGap: 14, maxGap: Infinity },
];

/** The car pace is measured against: the Tom's. */
export const PACE_REFERENCE = 'toms-gt500';

/** Every car's category, by car id. A car missing here cannot post a score. */
export const CAR_CATEGORY: Readonly<Record<string, CategoryId>> = {
  'toms-gt500': 'gt',
  'tengu-gt-x': 'gt',
  'kaze-b4': 'sport',
  yellowbird: 'sport',
  hellcat: 'sport',
  'drift-supra': 'sport',
  silvia: 'sport',
  'onibi-silhouette': 'street',
  'kaido-zen-r': 'street',
  'nsx-gt500': 'gt',
  m4: 'sport',
  'rx7-fd': 'street',
};

export function isCategory(value: string): value is CategoryId {
  return CATEGORIES.some((c) => c.id === value);
}

export function categoryOf(carId: string): Category | undefined {
  const id = CAR_CATEGORY[carId];
  return id === undefined ? undefined : CATEGORIES.find((c) => c.id === id);
}

/** The ids of every car in a category. */
export function carsIn(category: CategoryId): string[] {
  return Object.keys(CAR_CATEGORY).filter((id) => CAR_CATEGORY[id] === category);
}
